import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Upload, CheckCircle2, Lock, Clock, AlertCircle } from "lucide-react";

function UploadTile({ label, docType, fileId, onUploaded }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", docType);
      const { data } = await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.file_id);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <button type="button" data-testid={`owner-upload-${docType}`} onClick={() => ref.current?.click()}
      className={`w-full border-2 border-dashed rounded-2xl p-5 flex flex-col items-center justify-center gap-2 text-center transition-colors ${fileId ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-orange-600 bg-slate-50"}`}>
      <input ref={ref} type="file" hidden accept="image/*,.pdf" onChange={onFile} />
      {fileId ? (
        <>
          <CheckCircle2 size={26} className="text-emerald-600" />
          <div className="font-semibold text-emerald-700 text-sm">Uploaded · click to replace</div>
        </>
      ) : (
        <>
          <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 grid place-items-center"><Upload size={18} className="text-orange-800" /></div>
          <div>
            <div className="font-semibold text-sm">{uploading ? "Uploading…" : label}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">JPG, PNG or PDF · Max 8MB</div>
          </div>
        </>
      )}
    </button>
  );
}

export default function OwnerIdentity() {
  const [me, setMe] = useState(null);
  const [aadhaarFile, setAadhaarFile] = useState("");
  const [aadhaarBackFile, setAadhaarBackFile] = useState("");
  const [panFile, setPanFile] = useState("");
  const [form, setForm] = useState({ aadhaar_number: "", pan_number: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/auth/me").then((r) => {
    setMe(r.data);
    const k = r.data?.kyc || {};
    setForm({ aadhaar_number: k.aadhaar_number || "", pan_number: k.pan_number || "" });
    // Restore previously uploaded file ids so the user can see they've already uploaded
    if (k.aadhaar_file_id) setAadhaarFile(k.aadhaar_file_id);
    if (k.aadhaar_back_file_id) setAadhaarBackFile(k.aadhaar_back_file_id);
    if (k.pan_file_id) setPanFile(k.pan_file_id);
  });
  useEffect(() => { load(); }, []);
  if (!me) return null;

  const status = me.kyc_status || "not_submitted";
  const verified = status === "verified";
  const pending = status === "pending";
  const rejected = status === "rejected";

  const submit = async (e) => {
    e.preventDefault();
    if (!aadhaarFile) { toast.error("Please upload the FRONT side of your Aadhaar"); return; }
    if (!aadhaarBackFile) { toast.error("Please upload the BACK side of your Aadhaar"); return; }
    const digits = form.aadhaar_number.replace(/\s/g, "");
    if (digits.length !== 12) { toast.error("Aadhaar must be 12 digits"); return; }
    setSubmitting(true);
    try {
      await api.post("/kyc/owner-submit", {
        aadhaar_number: digits,
        aadhaar_file_id: aadhaarFile,
        aadhaar_back_file_id: aadhaarBackFile,
        pan_number: form.pan_number.trim().toUpperCase(),
        pan_file_id: panFile,
      });
      toast.success("Submitted for verification — HostelNow team will review within 24 hours");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  return (
    <section className="mt-10">
      <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Identity</div>
      <h2 className="font-display font-black text-3xl tracking-tighter mt-1 flex items-center gap-2">
        <ShieldCheck size={26} className="text-orange-800" /> Owner Aadhaar verification
      </h2>

      {verified && (
        <div data-testid="owner-kyc-verified" className="mt-5 bg-emerald-50 border border-emerald-200 rounded-2xl p-6 flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
            <ShieldCheck size={22} />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg">You&apos;re verified ✓</div>
            <p className="text-sm text-slate-600 mt-1">
              Reviewed by HostelNow admin
              {me.kyc_reviewed_by && <> · <span className="font-mono text-xs">{me.kyc_reviewed_by}</span></>}
            </p>
            <p className="text-xs text-slate-500 mt-2">Verified owners get a green badge on their listings — students trust verified owners more.</p>
          </div>
        </div>
      )}

      {pending && (
        <div data-testid="owner-kyc-pending" className="mt-5 bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-amber-100 text-amber-700 grid place-items-center">
            <Clock size={22} />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg">Under review</div>
            <p className="text-sm text-slate-600 mt-1">Our admin team is manually verifying your documents. This usually takes under 24 hours.</p>
          </div>
        </div>
      )}

      {!verified && !pending && (
        <>
          {rejected && me.kyc_rejection_reason && (
            <div data-testid="owner-kyc-rejected" className="mt-5 bg-rose-50 border-2 border-rose-300 rounded-2xl p-5 flex items-start gap-3">
              <AlertCircle className="text-rose-600 flex-shrink-0 mt-0.5" size={22} />
              <div>
                <div className="font-bold text-rose-900">Previous submission was rejected</div>
                <p className="text-sm text-rose-900 mt-1">Reason: <span className="font-semibold">{me.kyc_rejection_reason}</span></p>
                <p className="text-xs text-rose-700 mt-2">Please re-upload clearer photos below and try again.</p>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-5 bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-amber-600">Reviewed by HostelNow team</div>
              <h3 className="mt-1 font-display font-black text-2xl">Submit your Aadhaar for verification.</h3>
              <p className="mt-1 text-sm text-slate-600 max-w-xl">Verified owners get a green badge and up to 4× more bookings. Our admin manually reviews every submission within 24 hours.</p>
            </div>

            <div>
              <Label className="text-xs tracking-widest uppercase font-bold">Aadhaar number</Label>
              <Input
                data-testid="owner-kyc-aadhaar"
                required
                inputMode="numeric"
                maxLength={14}
                value={form.aadhaar_number}
                onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value })}
                placeholder="1234 5678 9012"
                className="mt-1 h-11"
              />
              <div className="text-[11px] text-slate-500 mt-2">
                Upload the <b>front</b> and <b>back</b> photos of your Aadhaar card separately. Both are required for verification.
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <UploadTile
                label="Aadhaar Front"
                docType="owner_aadhaar"
                fileId={aadhaarFile}
                onUploaded={setAadhaarFile}
              />
              <UploadTile
                label="Aadhaar Back"
                docType="owner_aadhaar_back"
                fileId={aadhaarBackFile}
                onUploaded={setAadhaarBackFile}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs tracking-widest uppercase font-bold">PAN number <span className="font-normal text-slate-400 normal-case">(optional)</span></Label>
                <Input
                  data-testid="owner-kyc-pan"
                  value={form.pan_number}
                  onChange={(e) => setForm({ ...form, pan_number: e.target.value.toUpperCase() })}
                  placeholder="ABCDE1234F"
                  maxLength={10}
                  className="mt-1 h-11 uppercase"
                />
              </div>
              <UploadTile label="Upload PAN (optional)" docType="owner_pan" fileId={panFile} onUploaded={setPanFile} />
            </div>

            <div className="flex items-center justify-between border-t border-slate-100 pt-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Lock size={14} className="text-orange-800" /> Documents encrypted at rest · admin-only access
              </div>
              <Button
                data-testid="owner-kyc-submit"
                type="submit"
                disabled={submitting}
                className="rounded-full bg-orange-800 hover:bg-orange-900 text-white h-11 px-6"
              >
                {submitting ? "Submitting…" : "Submit for verification"}
              </Button>
            </div>
          </form>
        </>
      )}
    </section>
  );
}
